/**
 * Cluster 3 — Screen I : Battery Contracts.
 *
 * Layout:
 *   - Tab strip
 *   - Active typed contracts (CIS / Tolling / FCAS) summary cards
 *   - Weekly revenue mode (Floor / Merchant / Share-above) — stacked bars
 *   - Tolling availability (peak window: dispatched / offered / unavailable)
 *   - Obligations tracker (per-row current vs threshold, status colour)
 *   - Required actions (auto-generated from threshold breaches)
 *
 * Data: GET /api/batteries/<duid>/contracts
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Card, Stack, Skeleton, Alert, Chip, Tooltip, Divider,
} from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { LineChart } from '@mui/x-charts/LineChart';
import axios from 'axios';

import { fmtAUDCompact, fmtMWhCompact, fmtAUDPerMWh } from './KpiCard';
import { useThemeColors } from '../theme/ThemeModeContext';
import TechnicalWorkspaceTabs from './TechnicalWorkspaceTabs';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

const STATUS_TINT = {
  green:    '#22c55e',
  amber:    '#f59e0b',
  red:      '#ef4444',
  unknown:  '#6b7280',
};

const KIND_TINT = {
  CIS:     '#f59e0b',
  Tolling: '#8b5cf6',
  FCAS:    '#ef4444',
  PPA:     '#D98A2B',
};

const SEVERITY_META = {
  critical: { color: '#ef4444', icon: ErrorOutlineIcon, label: 'CRITICAL' },
  warning:  { color: '#f59e0b', icon: WarningAmberIcon, label: 'WARNING' },
  info:     { color: '#3b82f6', icon: CheckCircleOutlineIcon, label: 'INFO' },
};

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtWeek(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' });
}


// ── Sub-components ──────────────────────────────────────────────────────

function ContractSummaryCard({ contract, colors }) {
  const tint = KIND_TINT[contract.contract_kind] || colors.textMute;
  const body = contract.body || {};

  const pillarLine = (() => {
    if (contract.contract_kind === 'CIS') {
      return `Floor ${fmtAUDPerMWh(body.floor_price_aud_per_mwh)} · Ceiling ${fmtAUDPerMWh(body.ceiling_price_aud_per_mwh)} · ${(body.revenue_share_above_pct * 100).toFixed(0)}% share above`;
    }
    if (contract.contract_kind === 'Tolling') {
      return `${body.contracted_mw} MW · peak ${body.peak_window_start_hour}:00–${body.peak_window_end_hour}:00${body.weekdays_only ? ' weekdays' : ''} · ${fmtAUDPerMWh(body.energy_fee_aud_per_mwh)} energy fee`;
    }
    if (contract.contract_kind === 'FCAS') {
      return `${body.service?.replace('_', ' ')} · ${body.contracted_mw} MW · A$${body.enablement_fee_aud_per_mw_h?.toFixed(2)}/MW/h`;
    }
    return '—';
  })();

  return (
    <Card sx={{ p: 2 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
        <Chip label={contract.contract_kind} size="small"
              sx={{ bgcolor: `${tint}1f`, color: tint,
                    fontWeight: 700, fontSize: '0.65rem', letterSpacing: '0.06em',
                    height: 20, borderRadius: '4px' }} />
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {contract.counterparty}
        </Typography>
      </Stack>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5,
                                        overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {pillarLine}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.disabled' }}>
        {fmtDate(contract.start_date)} → {fmtDate(contract.end_date)}
      </Typography>
    </Card>
  );
}


function ObligationRow({ ob, colors }) {
  const color = STATUS_TINT[ob.status] || colors.textMute;
  const onTarget = ob.status === 'green';
  return (
    <Box sx={{
      px: 2, py: 1.5,
      borderBottom: '1px solid', borderColor: 'divider',
      '&:last-child': { borderBottom: 'none' },
    }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.4 }}>
            <FiberManualRecordIcon sx={{ fontSize: 9, color }} />
            <Typography sx={{ fontWeight: 600, fontSize: '0.92rem', color: 'text.primary' }}>
              {ob.name}
            </Typography>
            <Chip
              label={ob.status.toUpperCase()}
              size="small"
              sx={{
                bgcolor: `${color}1f`, color,
                fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.06em',
                height: 18, borderRadius: '4px',
              }}
            />
          </Stack>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            {ob.contract_kind} · {ob.counterparty}
          </Typography>
        </Box>
        <Stack alignItems="flex-end" spacing={0.25} sx={{ flexShrink: 0, minWidth: 200 }}>
          <Stack direction="row" spacing={0.75} alignItems="baseline">
            <Typography sx={{
              fontWeight: 700, fontSize: '1.05rem',
              color: onTarget ? 'text.primary' : color,
              fontVariantNumeric: 'tabular-nums',
            }}>
              {ob.current != null ? `${ob.current}${ob.unit === 'cycles' ? '' : ob.unit}` : '—'}
              {ob.unit === 'cycles' && ' cycles'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              vs {ob.threshold}{ob.unit === 'cycles' ? '' : ob.unit}{ob.unit === 'cycles' && ' target'}
            </Typography>
          </Stack>
          {/* Progress bar */}
          {ob.current != null && ob.threshold && (
            <Box sx={{
              height: 4, borderRadius: 2, bgcolor: colors.track,
              overflow: 'hidden', width: 180,
            }}>
              <Box sx={{
                height: '100%',
                width: `${Math.min((ob.current / ob.threshold) * 100, 100)}%`,
                bgcolor: color, opacity: 0.85,
                transition: 'width 240ms ease',
              }} />
            </Box>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}


// ── Page ─────────────────────────────────────────────────────────────────

export default function BatteryContracts() {
  const { portfolioId } = useParams();
  const [searchParams] = useSearchParams();
  const colors = useThemeColors();
  const duid = (searchParams.get('duid') || 'VBB1').toUpperCase();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    // portfolio_id is required — contracts are read from this portfolio only,
    // so a DUID shared across portfolios doesn't pull in the others' terms.
    axios.get(`${API_BASE_URL}/api/batteries/${duid}/contracts`,
              { params: { portfolio_id: portfolioId } })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.error || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [duid, portfolioId]);

  // Weekly revenue mode chart prep
  const { weekXs, floorAud, merchantAud, shareAud, upsideKeepAud } = useMemo(() => {
    const arr = data?.weekly_revenue_mode || [];
    return {
      weekXs:        arr.map((w) => new Date(w.week)),
      floorAud:      arr.map((w) => Number((w.floor_aud       || 0).toFixed(0))),
      merchantAud:   arr.map((w) => Number((w.merchant_aud    || 0).toFixed(0))),
      upsideKeepAud: arr.map((w) => Number((w.upside_keep_aud || 0).toFixed(0))),
      shareAud:      arr.map((w) => Number((w.share_aud       || 0).toFixed(0))),
    };
  }, [data]);

  const tolling = data?.tolling_summary;
  const tollingMaxBar = tolling ? tolling.total_mwh : 0;

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
      <TechnicalWorkspaceTabs />

      {/* Header */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.025em', color: 'text.primary' }}>
          {data?.asset?.name || duid}
        </Typography>
        {data?.asset && (
          <Chip label={`${data.asset.capacity_mw?.toFixed(0)} MW · ${data.asset.storage_mwh?.toFixed(0)} MWh`}
                size="small" variant="outlined"
                sx={{ borderRadius: '4px', fontWeight: 600, fontSize: '0.72rem', height: 22 }} />
        )}
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Battery Contracts · {data?.period?.label || '—'}
        </Typography>
      </Stack>

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

      {/* Active contracts row */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="overline" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
          Active contracts on {duid}
        </Typography>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
          gap: 1.5,
        }}>
          {loading ? (
            [1,2,3].map((i) => (
              <Card key={i} sx={{ p: 2 }}>
                <Skeleton width="40%" sx={{ bgcolor: colors.skeleton, mb: 1 }} />
                <Skeleton width="80%" sx={{ bgcolor: colors.skeleton, mb: 0.5 }} />
                <Skeleton width="60%" sx={{ bgcolor: colors.skeleton }} />
              </Card>
            ))
          ) : !data?.contracts?.length ? (
            <Card sx={{ p: 3, textAlign: 'center', color: 'text.disabled' }}>
              <Typography>No active contracts on this DUID.</Typography>
            </Card>
          ) : (
            data.contracts.map((c) => <ContractSummaryCard key={c.id} contract={c} colors={colors} />)
          )}
        </Box>
      </Box>

      {/* Weekly revenue mode + Tolling availability */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2, mb: 2 }}>
        <Card sx={{ p: 2.25 }}>
          <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="overline" sx={{ color: 'text.secondary' }}>
              Weekly revenue mode
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              CIS Floor / Merchant / Share-above · stacked
            </Typography>
          </Stack>
          {loading ? (
            <Skeleton variant="rectangular" height={240} sx={{ bgcolor: colors.skeleton, borderRadius: 1 }} />
          ) : weekXs.length === 0 ? (
            <Box sx={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography color="text.disabled">
                No CIS contract on this DUID — weekly mode chart needs CIS terms.
              </Typography>
            </Box>
          ) : (
            <LineChart
              height={240}
              xAxis={[{
                data: weekXs, scaleType: 'time',
                valueFormatter: (v) => fmtWeek(v?.toISOString?.()),
                tickLabelStyle: { fill: colors.textDim, fontSize: 11 },
              }]}
              yAxis={[{
                tickLabelStyle: { fill: colors.textDim, fontSize: 11 },
                valueFormatter: (v) => fmtAUDCompact(v),
              }]}
              series={[
                { id: 'floor',    data: floorAud,      label: 'Floor (CIS)',  color: '#f59e0b',
                  area: true, stack: 'rev', showMark: false,
                  valueFormatter: (v) => fmtAUDCompact(v) },
                { id: 'merchant', data: merchantAud,   label: 'Merchant',     color: '#E9B065',
                  area: true, stack: 'rev', showMark: false,
                  valueFormatter: (v) => fmtAUDCompact(v) },
                { id: 'upside',   data: upsideKeepAud, label: 'Above ceiling — keep', color: '#D98A2B',
                  area: true, stack: 'rev', showMark: false,
                  valueFormatter: (v) => fmtAUDCompact(v) },
                { id: 'share',    data: shareAud,      label: 'Above ceiling — share',  color: '#3b82f6',
                  area: true, stack: 'rev', showMark: false,
                  valueFormatter: (v) => fmtAUDCompact(v) },
              ]}
              margin={{ top: 16, right: 24, bottom: 30, left: 64 }}
              slotProps={{ legend: { direction: 'horizontal', position: { vertical: 'top', horizontal: 'center' } } }}
              sx={{
                '& .MuiChartsAxis-line':   { stroke: colors.hairline2 },
                '& .MuiChartsAxis-tick':   { stroke: colors.hairline2 },
                '& .MuiChartsGrid-line':   { stroke: colors.hairline },
                '& .MuiChartsLegend-label':{ fill: colors.textDim, fontSize: '0.75rem' },
              }}
            />
          )}
        </Card>

        <Card sx={{ p: 2.25 }}>
          <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="overline" sx={{ color: 'text.secondary' }}>
              Tolling availability
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              Peak window
            </Typography>
          </Stack>
          {loading ? (
            <Skeleton variant="rectangular" height={200} sx={{ bgcolor: colors.skeleton, borderRadius: 1 }} />
          ) : !tolling ? (
            <Typography color="text.disabled" sx={{ py: 4, textAlign: 'center' }}>
              No tolling contract on this DUID.
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {[
                { key: 'dispatched',  label: 'Dispatched',  color: '#22c55e' },
                { key: 'offered',     label: 'Offered (online, not dispatched)', color: '#3b82f6' },
                { key: 'unavailable', label: 'Unavailable',  color: '#ef4444' },
              ].map((row) => {
                const mwh = tolling[`${row.key}_mwh`];
                const pct = tolling[`${row.key}_pct`];
                return (
                  <Box key={row.key}>
                    <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.4 }}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <FiberManualRecordIcon sx={{ fontSize: 9, color: row.color }} />
                        <Typography variant="body2" sx={{ color: 'text.primary' }}>
                          {row.label}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="baseline">
                        <Typography sx={{ fontWeight: 600, fontSize: '0.85rem',
                                          fontVariantNumeric: 'tabular-nums', color: 'text.primary' }}>
                          {fmtMWhCompact(mwh)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary',
                                                            minWidth: 42, textAlign: 'right',
                                                            fontVariantNumeric: 'tabular-nums' }}>
                          {pct.toFixed(1)}%
                        </Typography>
                      </Stack>
                    </Stack>
                    <Box sx={{ height: 6, borderRadius: 3, bgcolor: colors.track, overflow: 'hidden' }}>
                      <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: row.color, opacity: 0.85 }} />
                    </Box>
                  </Box>
                );
              })}
              <Divider sx={{ borderColor: 'divider' }} />
              <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Total peak-window energy</Typography>
                <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 600 }}>
                  {fmtMWhCompact(tolling.total_mwh)}
                </Typography>
              </Stack>
            </Stack>
          )}
        </Card>
      </Box>

      {/* Obligations tracker */}
      <Card sx={{ p: 0, mb: 2 }}>
        <Stack direction="row" alignItems="baseline" justifyContent="space-between"
               sx={{ px: 2.25, py: 1.75, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="overline" sx={{ color: 'text.secondary' }}>
            Contractual obligations — live tracking
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            {data?.obligations?.length || 0} obligations · 30-day rolling
          </Typography>
        </Stack>
        {loading ? (
          <Box sx={{ p: 2 }}>
            {[1,2,3,4,5].map((i) => (
              <Skeleton key={i} variant="rectangular" height={36} sx={{ bgcolor: colors.skeleton, mb: 1 }} />
            ))}
          </Box>
        ) : !data?.obligations?.length ? (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.disabled' }}>
            <Typography>No obligations defined.</Typography>
          </Box>
        ) : (
          data.obligations.map((ob, i) => <ObligationRow key={i} ob={ob} colors={colors} />)
        )}
      </Card>

      {/* Required actions */}
      <Card sx={{ p: 0 }}>
        <Stack direction="row" alignItems="baseline" justifyContent="space-between"
               sx={{ px: 2.25, py: 1.75, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="overline" sx={{ color: 'text.secondary' }}>
            Required actions
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            Auto-generated from threshold breaches
          </Typography>
        </Stack>
        {loading ? (
          <Box sx={{ p: 2 }}>
            <Skeleton variant="rectangular" height={50} sx={{ bgcolor: colors.skeleton, mb: 1 }} />
            <Skeleton variant="rectangular" height={50} sx={{ bgcolor: colors.skeleton }} />
          </Box>
        ) : !data?.required_actions?.length ? (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.disabled' }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 32, mb: 0.5, color: 'success.main' }} />
            <Typography>All obligations on target.</Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 0.25 }}>
              No actions required.
            </Typography>
          </Box>
        ) : (
          data.required_actions.map((a, i) => {
            const meta = SEVERITY_META[a.severity] || SEVERITY_META.info;
            const Icon = meta.icon;
            return (
              <Box key={i} sx={{
                display: 'flex', alignItems: 'flex-start', gap: 1.5,
                px: 2, py: 1.5,
                borderBottom: '1px solid', borderColor: 'divider',
                '&:last-child': { borderBottom: 'none' },
              }}>
                <Icon sx={{ fontSize: 20, color: meta.color, mt: 0.25 }} />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 0.25 }}>
                    <Chip label={meta.label} size="small"
                          sx={{ bgcolor: `${meta.color}1f`, color: meta.color,
                                fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.06em',
                                height: 18, borderRadius: '4px' }} />
                    <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>{a.title}</Typography>
                  </Stack>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {a.detail}
                  </Typography>
                </Box>
              </Box>
            );
          })
        )}
      </Card>
    </Box>
  );
}
